import React, { useEffect, useState, useRef } from 'react';
import pb from './lib/pocketbase';
import { useHighlightsData } from './context/HighlightsContext';
import { useHomeData } from './context/HomeContext';
import { useTechnologiesData } from './context/TechnologiesContext';
import { useSiteSettings } from './context/SiteSettingsContext';
import './RecruitmentComponents.css'; // For the loader styles
import {
  FiMonitor, FiLayers, FiSmartphone,
  FiChevronLeft, FiChevronRight,
  FiCheckCircle, FiTrendingUp, FiArrowRight,
  FiCode, FiServer, FiCloud, FiDatabase,
  FiUsers, FiMessageSquare, FiPlus, FiMinus,
  FiX, FiMessageCircle, FiSmile, FiChevronDown,
  FiSettings, FiGitMerge, FiShield, FiTool,
  FiClock, FiMapPin, FiTarget
} from 'react-icons/fi';
import { FaJava } from 'react-icons/fa';
import {
  SiReact, SiNodedotjs, SiPhp, SiDotnet,
  SiDocker, SiAmazonwebservices, SiGooglecloud, SiGitlab,
  SiTypescript, SiPostgresql, SiNextdotjs
} from 'react-icons/si';
import { Link } from 'react-router-dom';
import './AgencySection.css';
import './FaqFullWidth.css';

/* --- Private Components --- */

function Counter({ end, duration = 2000, label, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime = null;
          const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) {
              window.requestAnimationFrame(step);
            }
          };
          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <div className="counter-item" ref={ref}>
      <div className="counter-value">
        {count}
        {suffix}
      </div>
      <div className="counter-label">{label}</div>
    </div>
  );
}

const IconMap = {
  FiMonitor, FiLayers, FiSmartphone, FiCode, FiServer, FiDatabase,
  FiCloud, FiTrendingUp, FiSettings, FiUsers, FiTarget, FiMessageSquare,
  FiCheckCircle, FiShield, FiTool, FiZap: FiTrendingUp
};

function MetiersSlider({ manualData }) {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const defaultSlides = [
    [
      {
        id: 'developpement',
        icon: <FiMonitor className="service-icon" />,
        title: 'Projets web métiers',
        description: 'Portails clients, extranets, applications métier sur mesure, intégrées à vos SI.',
      },
      {
        id: 'ged-metiers',
        icon: <FiLayers className="service-icon" />,
        title: 'Dématérialisation & GED',
        description: 'Solutions de Gestion Électronique des Documents et workflows métiers.',
      },
      {
        id: 'mobilite-metiers',
        icon: <FiSmartphone className="service-icon" />,
        title: 'Mobilité & services connectés',
        description: 'Expériences mobiles optimisées, synchronisées avec vos applications.',
      },
    ],
    [
      {
        id: 'architecture',
        icon: <FiLayers className="service-icon" />,
        title: 'Architecture',
        description: "Conception d'architectures robustes, scalables et sécurisées.",
      },
      {
        id: 'reseau',
        icon: <FiServer className="service-icon" />,
        title: 'Réseau & exploitation',
        description: 'Supervision, sécurité et performance de vos environnements de production.',
      },
      {
        id: 'design',
        icon: <FiMonitor className="service-icon" />,
        title: 'Design & ergonomie',
        description: 'Interfaces modernes, accessibles et centrées sur vos utilisateurs.',
      },
    ],
  ];

  let slides = defaultSlides;
  if (manualData && Array.isArray(manualData) && manualData.length > 0) {
    slides = [];
    for (let i = 0; i < manualData.length; i += 3) {
      slides.push(manualData.slice(i, i + 3).map((item, idx) => ({
        id: `manual-${i + idx}`,
        icon: (() => {
          const IconComp = IconMap[item.icon] || FiMonitor;
          return <IconComp className="service-icon" />;
        })(),
        title: item.title,
        description: item.desc
      })));
    }
  }

  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isHovered, slides.length]);

  return (
    <div
      className="metiers-slider-shell"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="metiers-slider-window">
        <div
          className="metiers-slider-track"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((group, slideIndex) => (
            <div className="metiers-slider-slide" key={slideIndex}>
              <div className="grid grid-3 services-grid">
                {group.map((item) => (
                  <Link
                    key={item.id}
                    to={`/metiers#${item.id}`}
                    className="service-card glass-card"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="service-icon-wrapper">
                      {item.icon}
                    </div>
                    <div className="service-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="metiers-slider-controls">
        <button type="button" onClick={prev} aria-label="Slide précédent" className="metiers-slider-arrow">
          <FiChevronLeft />
        </button>
        <div className="metiers-slider-dots" aria-hidden="true">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`metiers-slider-dot ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
        <button type="button" onClick={next} aria-label="Slide suivant" className="metiers-slider-arrow">
          <FiChevronRight />
        </button>
      </div>
    </div>
  );
}

function FaqItem({ question, answer, icon, color }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div
      className={`faq-card ${isOpen ? 'open' : ''}`}
      onClick={() => setIsOpen(!isOpen)}
      style={{ '--card-accent-color': color }}
    >
      <button className="faq-question">
        <div className="faq-icon-wrapper">
          {icon}
        </div>
        <span className="faq-title">{question}</span>
        <FiPlus className="faq-toggle-icon" />
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
}

function ChatWidget({ data }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpenedAuto, setHasOpenedAuto] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!hasOpenedAuto) {
        setIsOpen(true);
        setHasOpenedAuto(true);
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [hasOpenedAuto]);

  if (!data) return null;

  const { title, subtitle, content, data: options } = data;

  const handleOption = (anchor) => {
    if (!anchor) return;
    setIsOpen(false);
    setTimeout(() => {
      if (anchor.startsWith('/')) {
        window.location.href = anchor;
        return;
      }
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className={`chat-widget ${isOpen ? 'open' : ''}`}>
      <button className="chat-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Assistant">
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </button>

      <div className="chat-window">
        <div className="chat-header">
          <div className="chat-avatar-frame">
            <img src="/images/logo.png" alt="Bot" />
          </div>
          <div className="chat-header-info">
            <strong>{title || 'Assistant Version Net'}</strong>
            <span>{subtitle || 'En ligne'}</span>
          </div>
          <button
            className="chat-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Fermer l'assistant"
          >
            <FiX />
          </button>
        </div>
        <div className="chat-body">
          <div className="chat-msg bot" dangerouslySetInnerHTML={{ __html: content || 'Bonjour ! 👋 <br /> Je peux vous orienter vers nos experts ?' }} />
          <div className="chat-options">
            {Array.isArray(options) && options.map((opt, i) => (
              <button key={i} onClick={() => handleOption(opt.anchor)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WelcomeCard() {
  const { highlights, loading } = useHighlightsData();
  const [slideIndex, setSlideIndex] = useState(0);

  // Filter only active highlights and map to component structure
  const slides = (highlights || [])
    .filter(h => h.is_active !== false)
    .map(h => ({
      id: h.id,
      type: h.label,
      image: h.image ? pb.files.getUrl(h, h.image) : null,
      text: h.title,
      subtext: h.subtitle,
      btnText: h.button_text || 'Découvrir',
      link: h.link || '/',
      colorClass: h.color || 'blue'
    }));

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (loading && slides.length === 0) {
    return (
      <div className="welcome-card loading-state">
        <div className="loader-visual" style={{ transform: 'scale(0.6)', margin: '0' }}>
          <div className="loader-orbit orbit-1"></div>
          <div className="loader-orbit orbit-2"></div>
          <div className="loader-orbit orbit-3"></div>
          <div className="loader-logo-wrapper">
            <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
          </div>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 500 }}>Chargement des actualités...</p>
      </div>
    );
  }

  if (slides.length === 0) return null;

  const currentSlide = slides[slideIndex] || slides[0];

  const nextSlide = () => {
    setSlideIndex((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="welcome-card fade-in-slide">
      <div className="welcome-card-header">
        <span className={`sc-marker marker-${currentSlide.colorClass}`}></span>
        <h3>{currentSlide.type}</h3>
        {slides.length > 1 && (
          <div className="slider-arrows">
            <button onClick={prevSlide} aria-label="Précédent"><FiChevronLeft /></button>
            <button onClick={nextSlide} aria-label="Suivant"><FiChevronRight /></button>
          </div>
        )}
      </div>
      <div className="welcome-card-body">
        <div key={currentSlide.id} className="slide-content animate-fade">
          {currentSlide.image && (
            <Link to={currentSlide.link} className="slide-image-wrapper">
              <img src={currentSlide.image} alt={currentSlide.type} className="slide-img" />
            </Link>
          )}

          <div className="slide-text">
            <h4>
              <Link to={currentSlide.link} style={{ textDecoration: 'none', color: 'inherit' }}>
                {currentSlide.text}
              </Link>
            </h4>
            {currentSlide.subtext && <span>{currentSlide.subtext}</span>}
          </div>
          <Link to={currentSlide.link} className={`btn btn-${currentSlide.colorClass}`}>
            {currentSlide.btnText}
          </Link>
        </div>

        {slides.length > 1 && (
          <div className="slider-dots">
            {slides.map((_, idx) => (
              <span
                key={idx}
                className={`slider-dot ${idx === slideIndex ? 'active' : ''}`}
                onClick={() => setSlideIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function TrustMarquee({ logos }) {
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const xRef = useRef(0);
  const rafRef = useRef(null);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);

  useEffect(() => {
    if (!contentRef.current || logos.length === 0) return;

    const animate = () => {
      // Only auto-scroll if not dragging
      if (!isDragging) {
        xRef.current -= 0.5; // Base speed
      }

      const content = contentRef.current;
      if (content) {
        // Logic for infinite loop
        // We have 2 sets of logos. Full width is 2 * singleSetWidth.
        // We want to loop when we pass singleSetWidth.
        const fullWidth = content.scrollWidth;
        const halfWidth = fullWidth / 2;

        // Reset if we've scrolled too far left
        if (Math.abs(xRef.current) >= halfWidth) {
          xRef.current += halfWidth;
        }
        // Reset if we've dragged too far right (positive)
        if (xRef.current > 0) {
          xRef.current -= halfWidth;
        }

        content.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isDragging, logos]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startXRef.current = e.pageX;
    lastXRef.current = xRef.current;
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const delta = e.pageX - startXRef.current;
    xRef.current = lastXRef.current + delta;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  };

  return (
    <div
      className="marquee-container"
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: 'grab', touchAction: 'none' }} // touchAction none helps on mobile
    >
      <div
        className="marquee-content"
        ref={contentRef}
        style={{ animation: 'none', width: 'max-content' }} // Ensure it doesn't wrap and override CSS animation
      >
        {logos.map((item) => (
          <div key={item.id} className="client-logo-item" title={item.client} style={{ userSelect: 'none', position: 'relative' }}>
            <img
              src={pb.files.getUrl(item, item.logo, { thumb: '0x64' })}
              alt={item.client}
              className="client-logo-img"
              draggable="false"
            />
            <Link
              to={`/references/${item.slug || item.id}`}
              className="client-logo-overlay"
              draggable="false"
            >
              <span className="client-logo-btn">Voir le projet</span>
            </Link>
          </div>
        ))}
        {/* Duplicates for infinite loop */}
        {logos.map((item) => (
          <div key={`${item.id}-dup`} className="client-logo-item" title={item.client} style={{ userSelect: 'none', position: 'relative' }}>
            <img
              src={pb.files.getUrl(item, item.logo, { thumb: '0x64' })}
              alt={item.client}
              className="client-logo-img"
              draggable="false"
            />
            <Link
              to={`/references/${item.slug || item.id}`}
              className="client-logo-overlay"
              draggable="false"
            >
              <span className="client-logo-btn">Voir le projet</span>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomePage() {
  const [logos, setLogos] = useState([]);
  const [spotlights, setSpotlights] = useState([]);
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const featuredScrollRef = useRef(null);
  const [featuredDirection, setFeaturedDirection] = useState(1); // 1 = forward, -1 = backward

  // Autoplay for Featured Realisations (Ping-pong)
  useEffect(() => {
    const track = featuredScrollRef.current;
    if (!track || spotlights.length === 0) return;

    const interval = setInterval(() => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      const step = 360;

      let nextDirection = featuredDirection;
      // Change direction if we reach roughly the end or the beginning
      if (featuredDirection === 1 && scrollLeft + clientWidth >= scrollWidth - 20) {
        nextDirection = -1;
      } else if (featuredDirection === -1 && scrollLeft <= 20) {
        nextDirection = 1;
      }

      if (nextDirection !== featuredDirection) {
        setFeaturedDirection(nextDirection);
      }

      track.scrollBy({ left: step * nextDirection, behavior: 'smooth' });
    }, 4500);

    return () => clearInterval(interval);
  }, [spotlights, featuredDirection]);

  // Dynamic Content Context
  const { sections, loading: sectionsLoading } = useHomeData();
  const { technologies } = useTechnologiesData();
  const { showContact } = useSiteSettings();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const logosRes = await pb.collection('references').getList(1, 50, {
          filter: 'logo != "" && published = true && in_slider = true',
          sort: '-created',
        });
        const uniqueClients = [];
        const uniqueLogos = [];
        logosRes.items.forEach(item => {
          if (item.client && !uniqueClients.includes(item.client)) {
            uniqueClients.push(item.client);
            uniqueLogos.push(item);
          }
        });
        setLogos(uniqueLogos);

        const spotRes = await pb.collection('references').getList(1, 50, {
          sort: '-created',
          filter: 'published = true && vedette = true',
        });
        setSpotlights(spotRes.items);
      } catch (err) {
        console.error("Error fetching home data", err);
      }
    };
    fetchData();
  }, []);

  const renderSection = (section) => {
    const { type, title, subtitle, content, btn_text, btn_link, image, data } = section;

    switch (type) {
      case 'welcome':
        return (
          <section className="section welcome-section" key={section.id}>
            <div className="section-inner welcome-grid">
              <div className="welcome-text-col">
                <div className="welcome-divider"></div>
                {title && <h2>{title}</h2>}
                {subtitle && <p className="welcome-lead" dangerouslySetInnerHTML={{ __html: subtitle }} />}
                {content && (
                  <div className="welcome-desc" dangerouslySetInnerHTML={{ __html: content }} />
                )}

                {data && Array.isArray(data) && data.length > 0 && (
                  <ul className="welcome-checklist">
                    {data.map((item, i) => (
                      <li key={i}>
                        <FiCheckCircle className="check-icon" />
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {btn_text && (
                  <Link to={btn_link || '/'} className="btn btn-primary" style={{ marginTop: '1.5rem' }}>
                    {btn_text}
                  </Link>
                )}
              </div>
              <div className="welcome-card-col">
                <WelcomeCard />
              </div>
            </div>
            <div className="welcome-bg-blob"></div>
          </section>
        );

      case 'trust':
        return logos.length > 0 && (
          <section className="trust-section" key={section.id}>
            <div className="trust-inner">
              <p className="trust-label">{title || 'Ils nous font confiance'}</p>
              <TrustMarquee logos={logos} />
            </div>
          </section>
        );

      case 'counters':
        // Utilise les données JSON pour les compteurs
        const countersData = data || [
          { end: 20, suffix: '+', label: "Années d'expérience" },
          { end: 100, suffix: '%', label: "Made in France" },
          { end: 3, suffix: '', label: "Agences en France" },
          { end: 500, suffix: '+', label: "Projets livrés" }
        ];
        return (
          <section className="counters-section" key={section.id}>
            <div className="section-inner counters-inner">
              {countersData.map((c, i) => (
                <Counter key={i} end={c.end} suffix={c.suffix} label={c.label} />
              ))}
            </div>
          </section>
        );

      case 'services':
        return (
          <section className="section" id="tech-target" key={section.id}>
            <div className="section-inner">
              <div className="section-header">
                <h2>{title || 'Nos domaines d\'intervention'}</h2>
                {subtitle && <p>{subtitle}</p>}
              </div>
              <MetiersSlider manualData={data} />
            </div>
          </section>
        );

      case 'featured':
        return spotlights.length > 0 && (
          <section className="section featured-section" key={section.id}>
            <div className="section-inner">
              <div className="section-header">
                <h2>{title || 'Nos Réalisations à la Une'}</h2>
                {subtitle && <p>{subtitle}</p>}
              </div>
              <div className="featured-slider-wrapper">
                <button onClick={() => featuredScrollRef.current?.scrollBy({ left: -360, behavior: 'smooth' })} className="slider-nav-btn prev"><FiChevronLeft size={24} /></button>
                <button onClick={() => featuredScrollRef.current?.scrollBy({ left: 360, behavior: 'smooth' })} className="slider-nav-btn next"><FiChevronRight size={24} /></button>
                <div ref={featuredScrollRef} className="featured-slider-track" style={{ display: 'flex', gap: '2rem', overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: '1rem', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {spotlights.map((item) => (
                    <article key={item.id} className="news-card feature-slide-item" style={{ flex: '0 0 350px', minWidth: '300px', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '1rem', padding: '1rem' }}>
                        {item.logo ? (
                          <img src={pb.files.getURL(item, item.logo)} alt={item.client} style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-muted)' }}>{(item.client?.[0] || 'R')}</div>
                        )}
                      </div>
                      <div className="news-tag" style={{ alignSelf: 'flex-start' }}>{item.secteur || 'Projet'}</div>
                      <h3 style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>{item.titre || item.client}</h3>
                      <p style={{ flex: 1, fontSize: '0.95rem', color: 'var(--text-muted)' }}>
                        {item.resume ? (item.resume.length > 100 ? item.resume.substring(0, 100) + '...' : item.resume) : 'Découvrez ce projet en détail...'}
                      </p>
                      <Link to={`/references/${item.slug}`} className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', paddingLeft: 0, marginTop: '1rem' }}>Voir le cas client →</Link>
                    </article>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <Link to={btn_link || "/references"} className="btn btn-primary">{btn_text || 'Voir toutes nos références'}</Link>
              </div>
            </div>
          </section>
        );

      case 'testimonials':
        const testimonialsData = data || [
          { quote: "Version Net a su comprendre nos enjeux métiers complexes...", author: "Directeur Digital", company: "Grand Groupe Bancaire" },
          { quote: "L'expertise technique de l'équipe...", author: "DRH", company: "Industrie Automobile" },
          { quote: "Une collaboration fluide...", author: "CTO", company: "Start-up FinTech" }
        ];
        return (
          <section className="section testimonials-section" key={section.id}>
            <div className="section-inner">
              <div className="testimonial-card">
                <div className="testimonial-icon"><FiMessageSquare /></div>
                <blockquote>"{testimonialsData[testimonialIndex]?.quote}"</blockquote>
                <div className="testimonial-author">
                  <strong>{testimonialsData[testimonialIndex]?.author}</strong>
                  <span>{testimonialsData[testimonialIndex]?.company}</span>
                </div>
                <div className="testimonial-dots">
                  {testimonialsData.map((_, idx) => (
                    <button key={idx} className={`dot ${idx === testimonialIndex ? 'active' : ''}`} onClick={() => setTestimonialIndex(idx)} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        );

      case 'recruitment':
        return (
          <section className="section agency-life-section" id="recrut-target" key={section.id}>
            <div className="section-inner agency-inner">
              <div className="agency-content">
                {title && <h2>{title}</h2>}
                {subtitle && <p className="agency-lead" dangerouslySetInnerHTML={{ __html: subtitle }} />}
                {content && <div className="agency-desc" dangerouslySetInnerHTML={{ __html: content }} />}

                {data && Array.isArray(data) && data.length > 0 && (
                  <ul className="welcome-checklist" style={{ margin: '1.5rem 0' }}>
                    {data.map((item, i) => (
                      <li key={i}>
                        <FiCheckCircle className="check-icon" />
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <Link to={btn_link || "/recrutement"} className="btn btn-primary btn-lg" style={{ marginTop: '0.5rem' }}>
                  {btn_text || 'Nos opportunités de carrière'}
                </Link>
              </div>
              <div className="agency-visual">
                <img src={image ? pb.files.getUrl(section, image) : "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"} alt={title} />
              </div>
            </div>
          </section>
        );

      case 'cta':
        return (
          <section className="final-cta-section" key={section.id}>
            <div className="section-inner cta-inner">
              <h2>{title || 'Vous avez un projet ambitieux ?'}</h2>
              {subtitle && <p>{subtitle}</p>}
              <Link to={(!showContact && (btn_link === '/contact' || !btn_link)) ? "/references" : (btn_link || "/contact")} className="btn btn-primary btn-lg">
                {btn_text || 'Discuter avec un expert'}
              </Link>
            </div>
          </section>
        );

      case 'faq':
        const faqData = data || [];
        return (
          <section key={section.id}>
            <div style={{ width: '100%', maxWidth: '1600px', margin: '0 auto', padding: '1rem', boxSizing: 'border-box' }}>
              <div className="faq-header">
                <h2>{title || 'Questions Fréquentes'}</h2>
                {subtitle && <p>{subtitle}</p>}
              </div>
              <div className="faq-grid">
                {faqData.map((f, i) => {
                  const IconComp = IconMap[f.icon] || FiSettings;
                  return (
                    <FaqItem
                      key={i}
                      icon={<IconComp />}
                      color={f.color || "#3b82f6"}
                      question={f.question}
                      answer={f.answer}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'elo':
        return (
          <section className="section elo-section no-pad-top" id="elo-target" key={section.id}>
            <div className="section-inner">
              <div className="elo-card">
                <div className="elo-content">
                  <div className="elo-badge">{subtitle || 'PARTENAIRE BUSINESS'}</div>
                  <h2>{title || 'Expertise ELO Digital Office'}</h2>
                  {content && <div className="elo-desc" dangerouslySetInnerHTML={{ __html: content }} />}

                  {data && Array.isArray(data) && data.length > 0 && (
                    <ul className="welcome-checklist elo-checklist" style={{ margin: '1.5rem 0', color: 'white' }}>
                      {data.map((item, i) => (
                        <li key={i} style={{ color: 'white' }}>
                          <FiCheckCircle className="check-icon" style={{ color: 'white' }} />
                          <span>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link to={btn_link || "/services#elo"} className="btn btn-white" style={{ marginTop: '0.5rem' }}>
                    {btn_text || 'Découvrir notre offre GED'}
                  </Link>
                </div>
                <div className="elo-visual">
                  <div className="elo-visual-stack">
                    <div className="elo-doc doc-1" />
                    <div className="elo-doc doc-2" />
                    <div className="elo-doc doc-3" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );

      case 'process':
        const processData = data || [
          { num: 1, title: 'Audit & Conseil', desc: 'Analyse de vos besoins...' },
          { num: 2, title: 'Conception UI/UX', desc: 'Maquettage, ergonomie...' },
          { num: 3, title: 'Développement Agile', desc: 'Sprints itératifs...' },
          { num: 4, title: 'Exploitation', desc: 'Déploiement, hébergement...' },
        ];
        return (
          <section className="section timeline-section" key={section.id}>
            <div className="section-inner">
              <div className="section-header" style={{ textAlign: 'center', margin: '0 auto 3rem' }}>
                <h2>{title || 'Notre Processus'}</h2>
                {subtitle && <p>{subtitle}</p>}
              </div>
              <div className="process-timeline">
                {processData.map((p, i) => (
                  <div className="process-step" key={i}>
                    <div className="process-icon">{p.num}</div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'stack':
        const stackCategories = ['Frontend', 'Backend', 'Data & Infra'];
        const activeTechList = (technologies || []).filter(t => t.is_active !== false);

        return (
          <section className="section tech-section" key={section.id}>
            <div className="section-inner">
              <div className="section-header">
                <h2>{title || 'Notre Stack Technique'}</h2>
                {subtitle && <p>{subtitle}</p>}
              </div>
              <div className="tech-grid">
                {stackCategories.map(cat => {
                  const catTechs = activeTechList.filter(t => t.category === cat);
                  if (activeTechList.length > 0 && catTechs.length === 0) return null;

                  return (
                    <div className="tech-category" key={cat}>
                      <h4>{cat}</h4>
                      <div className="tech-icons">
                        {catTechs.length > 0 ? (
                          catTechs.map(t => (
                            <div className="tech-item" key={t.id}>
                              <div className="tech-icon">
                                {t.logo ? (
                                  <img
                                    src={pb.files.getUrl(t, t.logo)}
                                    alt={t.name}
                                  />
                                ) : (
                                  <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{t.name.substring(0, 2).toUpperCase()}</span>
                                )}
                              </div>
                              <div className="tech-details">
                                <span className="tech-name">{t.name}</span>
                                {t.subtitle && <span className="tech-desc">{t.subtitle}</span>}
                              </div>
                            </div>
                          ))
                        ) : (
                          // Fallback static icons
                          cat === 'Frontend' ? (
                            <>
                              <div className="tech-icon"><SiReact title="React" /></div>
                              <div className="tech-icon"><SiNextdotjs title="Next.js" /></div>
                              <div className="tech-icon"><SiTypescript title="TypeScript" /></div>
                            </>
                          ) : cat === 'Backend' ? (
                            <>
                              <div className="tech-icon"><SiNodedotjs title="Node.js" /></div>
                              <div className="tech-icon"><SiPhp title="PHP" /></div>
                              <div className="tech-icon"><FaJava title="Java" /></div>
                              <div className="tech-icon"><SiDotnet title=".NET" /></div>
                            </>
                          ) : (
                            <>
                              <div className="tech-icon"><SiPostgresql title="PostgreSQL" /></div>
                              <div className="tech-icon"><SiDocker title="Docker" /></div>
                              <div className="tech-icon"><SiAmazonwebservices title="AWS" /></div>
                              <div className="tech-icon"><SiGooglecloud title="Google Cloud" /></div>
                            </>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  const assistantData = sections.find(s => s.type === 'assistant');

  return (
    <>
      {sections.length > 0 ? (
        sections.filter(s => s.type !== 'assistant').map(section => renderSection(section))
      ) : (
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <p>Configuration de l'accueil en cours...</p>
        </div>
      )}
      <ChatWidget data={assistantData} />
    </>
  );
}

export default HomePage;
